import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAddresses } from '../context/AddressContext';
import api from '../api';
import './PayoffCalculator.css';

// A property normally has one loan, but two lenders on one house is a real
// case (two Promissory Notes in the closing pack). Every address therefore
// carries an ordered list of loans; loan 1 always exists as the baseline
// card even before anything is extracted.
const emptyLoan = (loanIndex) => ({ loanIndex, lenderId: null, fieldData: {}, extractedLender: null });
const loanKey = (address, loanIndex) => `${address}#${loanIndex}`;

export default function PayoffCalculator() {
  const { id: packageId } = useParams();
  const { addresses } = useAddresses();
  const [fields, setFields] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [dataByAddress, setDataByAddress] = useState({}); // { [addr]: { loans: [ { loanIndex, lenderId, fieldData, extractedLender } ] } }
  const [files, setFiles] = useState([]);
  const [filling, setFilling] = useState({});      // keyed by loanKey
  const [extracting, setExtracting] = useState({}); // keyed by address
  const [error, setError] = useState('');
  const [cardErrors, setCardErrors] = useState({});     // keyed by address
  const [cardWarnings, setCardWarnings] = useState({}); // keyed by loanKey
  const [templateMissing, setTemplateMissing] = useState(false);
  const [effectiveThroughDate, setEffectiveThroughDate] = useState('');
  const saveTimers = useRef({});
  const globalSaveTimer = useRef(null);

  const validAddresses = addresses.filter(a => a.trim());

  const getLoans = (address) => {
    const loans = dataByAddress[address]?.loans;
    return loans && loans.length > 0 ? loans : [emptyLoan(1)];
  };

  const loadFiles = useCallback(async () => {
    try {
      const res = await api.get('/payoff/files', { params: { packageId } });
      setFiles(res.data.payoffs || []);
    } catch (err) {
      console.error('Failed to load payoff files:', err);
    }
  }, [packageId]);

  // Template fields (one-time)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/payoff/template-fields');
        if (cancelled) return;
        setFields(res.data.fields || []);
        setTemplateMissing(false);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 400) {
          setTemplateMissing(true);
          setFields([]);
        } else {
          setError('Failed to load payoff template fields.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lenders
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/lenders');
        if (!cancelled) setLenders(res.data.lenders || []);
      } catch (err) {
        console.error('Failed to load lenders:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Saved data per address. Re-runs whenever the global effective-through
  // date changes so server-side computed fields (Total Payoff Amount,
  // Per Diem Adjustment Date, etc.) refresh in place.
  const loadPayoffData = useCallback(async () => {
    try {
      const res = await api.get('/payoff/load-data', { params: { packageId } });
      setDataByAddress(res.data.data || {});
    } catch (err) {
      console.error('Failed to load payoff data:', err);
    }
  }, [packageId]);

  useEffect(() => {
    loadPayoffData();
  }, [loadPayoffData]);

  // Load the pack's saved global effective-through date
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/packages/${packageId}`);
        if (!cancelled) {
          setEffectiveThroughDate(res.data.package?.payoff_effective_through_date || '');
        }
      } catch (err) {
        // Ignored — package fetch errors handled elsewhere
      }
    })();
    return () => { cancelled = true; };
  }, [packageId]);

  const updateEffectiveThroughDate = (value) => {
    setEffectiveThroughDate(value);
    if (globalSaveTimer.current) clearTimeout(globalSaveTimer.current);
    globalSaveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/packages/${packageId}`, {
          payoff_effective_through_date: value || null,
        });
        // Reload data so the server-side overlay recomputes Total Payoff
        // Amount and the related per-diem fields with the new date
        await loadPayoffData();
      } catch (err) {
        console.error('Failed to save effective-through date:', err);
      }
    }, 400);
  };

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const debouncedSave = useCallback((address, loan) => {
    const key = loanKey(address, loan.loanIndex);
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(() => {
      api.post('/payoff/save-data', {
        packageId,
        address,
        loanIndex: loan.loanIndex,
        lenderId: loan.lenderId || null,
        fieldData: loan.fieldData || {},
      }).catch(err => console.error('Auto-save payoff data failed:', err));
    }, 800);
  }, [packageId]);

  // Apply `mutate` to one loan of an address (creating loan 1 if the
  // address has nothing saved yet), persist it, and return the new state.
  const updateLoan = (address, loanIndex, mutate) => {
    setDataByAddress(prev => {
      const currentLoans = prev[address]?.loans?.length ? prev[address].loans : [emptyLoan(1)];
      let target = currentLoans.find(l => l.loanIndex === loanIndex);
      if (!target) target = emptyLoan(loanIndex);
      const updated = mutate(target);
      const loans = currentLoans.some(l => l.loanIndex === loanIndex)
        ? currentLoans.map(l => (l.loanIndex === loanIndex ? updated : l))
        : [...currentLoans, updated].sort((a, b) => a.loanIndex - b.loanIndex);
      debouncedSave(address, updated);
      return { ...prev, [address]: { ...(prev[address] || {}), loans } };
    });
  };

  const updateField = (address, loanIndex, fieldName, value) => {
    updateLoan(address, loanIndex, loan => ({
      ...loan,
      fieldData: { ...loan.fieldData, [fieldName]: value },
    }));
  };

  const updateLender = (address, loanIndex, lenderId) => {
    updateLoan(address, loanIndex, loan => ({ ...loan, lenderId: lenderId || null }));
  };

  // One click reads the closing pack and populates every loan it finds. The
  // server returns one entry per Promissory Note; loans already saved beyond
  // that count are kept (never auto-deleted) so a missed note on a re-run
  // can't wipe out the user's data.
  const handleExtractFromClosingPack = async (address) => {
    setCardErrors(prev => ({ ...prev, [address]: '' }));
    setCardWarnings(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { if (k.startsWith(`${address}#`)) delete next[k]; });
      return next;
    });
    setExtracting(s => ({ ...s, [address]: true }));
    try {
      const res = await api.post('/payoff/extract-from-closing-pack', { packageId, address });
      const returned = res.data.loans || [];
      setDataByAddress(prev => {
        const currentLoans = prev[address]?.loans || [];
        const byIndex = new Map(currentLoans.map(l => [l.loanIndex, l]));
        returned.forEach(r => {
          const existing = byIndex.get(r.loanIndex) || emptyLoan(r.loanIndex);
          byIndex.set(r.loanIndex, {
            ...existing,
            lenderId: r.lenderId ?? existing.lenderId,
            fieldData: { ...existing.fieldData, ...(r.fieldData || {}) },
            extractedLender: r.extractedLender || existing.extractedLender,
          });
        });
        const loans = [...byIndex.values()].sort((a, b) => a.loanIndex - b.loanIndex);
        return { ...prev, [address]: { ...(prev[address] || {}), loans } };
      });
      const warnings = {};
      returned.forEach(r => {
        if (r.warning) warnings[loanKey(address, r.loanIndex)] = r.warning;
      });
      if (Object.keys(warnings).length > 0) {
        setCardWarnings(prev => ({ ...prev, ...warnings }));
      }
    } catch (err) {
      // Surface the real error so we can diagnose without server logs:
      // server-returned error → HTTP status text → exception message → generic
      const serverMsg = err.response?.data?.error;
      const status = err.response?.status;
      const fallback = err.message || 'Auto-fill from closing pack failed.';
      const msg = serverMsg
        ? `${serverMsg}${status ? ` (HTTP ${status})` : ''}`
        : status
          ? `Auto-fill failed: HTTP ${status}`
          : fallback;
      setCardErrors(prev => ({ ...prev, [address]: msg }));
    } finally {
      setExtracting(s => ({ ...s, [address]: false }));
    }
  };

  const handleFillPdf = async (address, loan) => {
    const key = loanKey(address, loan.loanIndex);
    setError('');
    setFilling(f => ({ ...f, [key]: true }));
    try {
      await api.post('/payoff/fill-pdf', {
        packageId,
        address,
        loanIndex: loan.loanIndex,
        lenderId: loan.lenderId || null,
        fieldData: loan.fieldData || {},
      });
      await loadFiles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fill payoff PDF.');
    } finally {
      setFilling(f => ({ ...f, [key]: false }));
    }
  };

  const handleRemoveLoan = async (address, loanIndex) => {
    if (!window.confirm(`Remove Loan ${loanIndex} for ${address}? This deletes its saved fields and any filled PDF.`)) return;
    try {
      await api.delete('/payoff/loan', { params: { packageId, address, loanIndex } });
      setDataByAddress(prev => {
        const loans = (prev[address]?.loans || []).filter(l => l.loanIndex !== loanIndex);
        return { ...prev, [address]: { ...(prev[address] || {}), loans } };
      });
      setCardWarnings(prev => {
        const next = { ...prev };
        delete next[loanKey(address, loanIndex)];
        return next;
      });
      await loadFiles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove loan.');
    }
  };

  const handleDownload = async (file) => {
    try {
      const res = await api.get(`/payoff/files/${file.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download.');
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete filled payoff for ${file.address}${file.loan_index > 1 ? ` (Loan ${file.loan_index})` : ''}?`)) return;
    try {
      await api.delete(`/payoff/files/${file.id}`);
      await loadFiles();
    } catch {
      setError('Failed to delete.');
    }
  };

  // Resolve the value to display for an auto-filled field
  const autoValue = (field, address, lender, fieldData) => {
    if (field.source === 'address') return address;
    if (field.source === 'lender' && lender && field.lenderField) {
      return lender[field.lenderField] || '';
    }
    if (field.source === 'computed') {
      return fieldData?.[field.name] || '';
    }
    return '';
  };

  const autoTagText = (source) => {
    if (source === 'address') return 'auto from address';
    if (source === 'lender') return 'auto from lender';
    if (source === 'computed') return 'auto-computed';
    return '';
  };

  return (
    <div className="payoff-page">
      <h1 className="page-title">Payoff Calculator</h1>

      {validAddresses.length === 0 ? (
        <div className="warning-box">No addresses saved. Go to the Addresses tab to add addresses first.</div>
      ) : templateMissing ? (
        <div className="warning-box">No loan payoff template uploaded. Go to Settings to upload a fillable PDF.</div>
      ) : (
        <div className="payoff-layout">
          <div className="payoff-main">
            <div className="payoff-global-card">
              <label className="payoff-global-label">Anticipated Closing Date (Payoff Effective Through Date)</label>
              <input
                type="date"
                value={effectiveThroughDate}
                onChange={e => updateEffectiveThroughDate(e.target.value)}
              />
              {!effectiveThroughDate && (
                <div className="payoff-global-warning">
                  Enter the anticipated closing date above. Total Payoff Amount and Per Diem Adjustment Date can&apos;t be calculated until this is set.
                </div>
              )}
            </div>

            {error && <div className="error-box">{error}</div>}

            {validAddresses.map(address => {
              const loans = getLoans(address);
              const multi = loans.length > 1;
              return (
                <div key={address} className="payoff-card">
                  <div className="payoff-card-header">
                    <h2 className="payoff-card-address">{address}</h2>
                    {multi && (
                      <span className="payoff-loan-count">{loans.length} loans on this property</span>
                    )}
                  </div>

                  {cardErrors[address] && (
                    <div className="error-box" style={{ marginBottom: 12 }}>{cardErrors[address]}</div>
                  )}

                  {loans.map(loan => {
                    const key = loanKey(address, loan.loanIndex);
                    const lender = loan.lenderId ? lenders.find(l => l.id === loan.lenderId) : null;
                    return (
                      <div key={key} className={multi ? 'payoff-loan payoff-loan-multi' : 'payoff-loan'}>
                        {multi && (
                          <div className="payoff-loan-header">
                            <span className="payoff-loan-title">Loan {loan.loanIndex} of {loans.length}</span>
                            {loan.loanIndex > 1 && (
                              <button
                                className="btn-link-danger"
                                onClick={() => handleRemoveLoan(address, loan.loanIndex)}
                                title="Remove this loan"
                              >
                                Remove loan
                              </button>
                            )}
                          </div>
                        )}

                        {loan.extractedLender ? (
                          <div className="payoff-extracted-lender">
                            Lender for this {multi ? 'loan' : 'property'} is: <strong>{loan.extractedLender}</strong>
                          </div>
                        ) : null}

                        <div className="payoff-card-row">
                          <div className="payoff-field wide">
                            <label>Lender</label>
                            <select
                              value={loan.lenderId || ''}
                              onChange={e => updateLender(address, loan.loanIndex, e.target.value ? Number(e.target.value) : null)}
                            >
                              <option value="">Select a lender...</option>
                              {lenders.map(l => (
                                <option key={l.id} value={l.id}>{l.lender_name}</option>
                              ))}
                            </select>
                            {lenders.length === 0 && (
                              <span className="hint">No lenders saved yet. Add one on the Lender Profiles page.</span>
                            )}
                          </div>
                        </div>

                        <div className="payoff-fields">
                          {fields.map(field => {
                            const isAuto = field.source !== 'user';
                            const value = isAuto
                              ? autoValue(field, address, lender, loan.fieldData)
                              : (loan.fieldData?.[field.name] || '');
                            const missingLender = field.source === 'lender' && !lender;
                            const missingComputed = field.source === 'computed' && !value;
                            const placeholder = missingLender
                              ? 'Select a lender above'
                              : missingComputed
                                ? 'Click "Auto-fill from Closing Pack"'
                                : '';
                            return (
                              <div key={field.name} className="payoff-field">
                                <label>
                                  {field.name}
                                  {isAuto && (
                                    <span className="auto-tag"> ({autoTagText(field.source)})</span>
                                  )}
                                </label>
                                <input
                                  type="text"
                                  value={value}
                                  onChange={
                                    isAuto
                                      ? undefined
                                      : e => updateField(address, loan.loanIndex, field.name, e.target.value)
                                  }
                                  readOnly={isAuto}
                                  placeholder={placeholder}
                                  className={isAuto ? 'auto-filled' : ''}
                                />
                              </div>
                            );
                          })}
                        </div>

                        {cardWarnings[key] && (
                          <div className="warning-box" style={{ marginBottom: 12 }}>{cardWarnings[key]}</div>
                        )}

                        <div className="payoff-card-actions">
                          {loan.loanIndex === 1 && (
                            <button
                              className="btn-secondary"
                              onClick={() => handleExtractFromClosingPack(address)}
                              disabled={!!extracting[address]}
                            >
                              {extracting[address] ? 'Reading closing pack...' : 'Auto-fill from Closing Pack'}
                            </button>
                          )}
                          <button
                            className="btn-primary"
                            onClick={() => handleFillPdf(address, loan)}
                            disabled={!!filling[key]}
                          >
                            {filling[key] ? 'Filling...' : multi ? `Fill PDF (Loan ${loan.loanIndex})` : 'Fill PDF'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <aside className="payoff-sidebar">
            <h2 className="sidebar-title">Filled Payoff Statements for this Refi Pack</h2>
            {files.length === 0 ? (
              <p className="sidebar-empty">No filled payoff statements yet. Fill out a property card and click "Fill PDF".</p>
            ) : (
              <ul className="payoff-list">
                {files.map(f => (
                  <li key={f.id} className="payoff-list-item">
                    <div className="payoff-list-info">
                      <div className="payoff-list-address">
                        {f.address}
                        {f.loan_index > 1 && <span className="payoff-list-loan"> · Loan {f.loan_index}</span>}
                      </div>
                      <div className="payoff-list-meta">{f.file_name}</div>
                    </div>
                    <div className="payoff-list-actions">
                      <button className="btn-icon" onClick={() => handleDownload(f)} title="Download">↓</button>
                      <button className="btn-icon btn-icon-danger" onClick={() => handleDelete(f)} title="Delete">×</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
